import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../config';

interface QueueItem {
  id: string;
  call_uuid: string;
  customer_phone: string;
  campaign: string;
  lead_id?: string;
  status: string;
  created_at: string;
  expires_at: string;
  name?: string;
  lead_status?: string;
  tier_score?: number;
  assigned_to?: string;
}

interface SSEMessage {
  type: 'connected' | 'queue_update' | 'error';
  agent_id?: string;
  queue?: QueueItem[];
  timestamp?: string;
  message?: string;
}

export const useCallQueueSSE = (agentId: string) => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId) {
      console.log('SSE: No agentId provided, skipping connection');
      return;
    }

    const sseUrl = API_ENDPOINTS.SSE.QUEUE(agentId);
    console.log('SSE: Connecting to', sseUrl);
    const eventSource = new EventSource(sseUrl);

    eventSource.onopen = () => {
      console.log('SSE: CONNECTION OPENED for agent:', agentId);
      setIsConnected(true);
      setError(null);
    };

    eventSource.onmessage = (event) => {
      console.log('SSE: MESSAGE RECEIVED:', event.data);
      setLastEvent(new Date().toISOString());
      
      try {
        const message: SSEMessage = JSON.parse(event.data);
        console.log('SSE: PARSED MESSAGE:', message);
        
        if (message.type === 'connected') {
          console.log('SSE: CONNECTED CONFIRMED for agent:', message.agent_id);
        } else if (message.type === 'queue_update' && message.queue) {
          console.log('SSE: QUEUE UPDATE -', message.queue.length, 'calls in queue');
          console.log('SSE: QUEUE DATA:', message.queue);
          setQueue(message.queue);
        } else if (message.type === 'error') {
          console.error('SSE: ERROR MESSAGE:', message.message);
          setError(message.message || 'Unknown error');
        }
      } catch (err) {
        console.error('SSE: FAILED TO PARSE MESSAGE:', err);
        console.error('SSE: RAW DATA:', event.data);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE: CONNECTION ERROR:', err);
      setIsConnected(false);
      setError('Connection error');
    };

    return () => {
      console.log('SSE: CLOSING CONNECTION for agent:', agentId);
      eventSource.close();
      setIsConnected(false);
    };
  }, [agentId]);

  const getWaitingCalls = useCallback(() => {
    const waiting = queue.filter(item => item.status === 'waiting');
    console.log('SSE: GET WAITING CALLS -', waiting.length, 'calls');
    return waiting;
  }, [queue]);

  return {
    queue,
    waitingCalls: getWaitingCalls(),
    isConnected,
    error,
    lastEvent,
  };
};
